import { useEffect, useState } from "react";
import { UserX } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * 149-cw-r151: Dialog konfirmasi Nonaktifkan Akun dengan alasan wajib.
 *
 * Digunakan di:
 *  - `AdminActionsCell.jsx` (User Management row)
 *  - `ProfileDialog.jsx` (Team & Divisions saat klik personil)
 *
 * Alasan disimpan di field `disabled_reason` + audit log security action.
 *
 * Props:
 *  - open, onOpenChange (bool controls)
 *  - userName (string) — nama target utk display
 *  - busy (bool) — network in-flight
 *  - onConfirm(reasonString) → parent lakukan POST /disable dgn `{reason}`
 */
const PRESET_REASONS = [
  "Resign",
  "PHK / Pemutusan Kerja",
  "Pensiun",
  "Cuti Panjang / Sabatikal",
  "Meninggal Dunia",
  "Kontrak Berakhir",
  "Mutasi Antar Perusahaan",
  "Lainnya",
];

export default function DisableAccountDialog({ open, onOpenChange, userName, busy = false, onConfirm }) {
  const [preset, setPreset] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) { setPreset(""); setNote(""); }
  }, [open]);

  const trimmedNote = note.trim();
  // Kalau preset "Lainnya" → alasan final = note (wajib). Kalau tidak → preset,
  // digabung dgn note kalau ada.
  const finalReason = preset === "Lainnya"
    ? trimmedNote
    : (trimmedNote ? `${preset} — ${trimmedNote}` : preset);
  const valid = preset && finalReason.length >= 3 && finalReason.length <= 200;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="rounded-none max-w-md" data-testid="dlg-disable-account">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight inline-flex items-center gap-2">
            <UserX size={18} className="text-rose-600" /> Nonaktifkan Akun
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-rose-50 border border-rose-200 p-3 text-[11px] text-rose-800">
            Anda akan menonaktifkan akun <b>{userName || "user ini"}</b>. Akun tidak bisa login sampai diaktifkan kembali. Semua sesi aktif langsung di-kick. Alasan tercatat di audit log &amp; history akun.
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Alasan Nonaktif <span className="text-rose-600">*</span>
            </label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger className="rounded-none mt-1" data-testid="disable-reason-preset">
                <SelectValue placeholder="Pilih alasan…" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {PRESET_REASONS.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`disable-reason-preset-opt-${r}`}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Detail / Catatan {preset === "Lainnya" && <span className="text-rose-600">*</span>}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 200))}
              rows={3}
              placeholder={preset === "Lainnya"
                ? "Sebutkan alasan (wajib, min 3 karakter)"
                : "Detail tambahan opsional (contoh: tanggal efektif, referensi surat memo, dll.)"}
              className="w-full border border-slate-300 rounded-none p-2 text-xs mt-1 focus:outline-none focus:border-[#0B1D3A]"
              data-testid="disable-reason-note"
              disabled={busy}
            />
            <div className="text-[9px] text-slate-400 mt-0.5 flex justify-between">
              <span>Max 200 karakter.</span>
              <span className="tabular-nums" data-testid="disable-reason-count">{note.length}/200</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)} disabled={busy} data-testid="disable-reason-cancel">
            Batal
          </Button>
          <Button
            data-testid="disable-reason-confirm"
            disabled={!valid || busy}
            onClick={() => onConfirm?.(finalReason)}
            className="rounded-none bg-rose-600 hover:bg-rose-700 text-white"
          >
            {busy ? "Menonaktifkan…" : "Konfirmasi Nonaktifkan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
